import React, { useState, useEffect } from 'react';
import EditableDataTable from '../EditableDataTable/EditableDataTable';
import ImportCapabilities from './ImportCapabilities';
import apiClient from '../../lib/apiClient';
import './Capabilities.css';

function Capabilities() {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Define all available columns with custom formatting
  const capabilityColumns = [
    { key: 'name', label: 'Name', required: true },
    { key: 'category', label: 'Category' },
    { key: 'source', label: 'Source' },
    { 
      key: 'cal_secure_phase', 
      label: 'Phase',
      type: 'number',
      format: (value) => value ? <span className={`phase-badge phase-${value}`}>Phase {value}</span> : '-'
    },
    { key: 'business_value', label: 'Business Value' },
    { key: 'description', label: 'Description' },
    { key: 'cisco_product', label: 'Cisco Product' },
    { key: 'cisco_security_suite', label: 'Cisco Security Suite' },
    { key: 'competitors', label: 'Competitors' },
    { key: 'gaps', label: 'Gaps' },
    { key: 'cisco_gartner_rating', label: 'Cisco Gartner Rating' },
    { key: 'cisco_forrester_rating', label: 'Cisco Forrester Rating' },
    { key: 'gartner_leaders', label: 'Gartner Leaders' },
    { key: 'forrester_leaders', label: 'Forrester Leaders' },
    { key: 'microsoft_solution', label: 'Microsoft Solution' },
    { 
      key: 'microsoft_pricing', 
      label: 'Microsoft Pricing',
      type: 'number',
      format: (value) => value ? `$${value.toLocaleString()}` : '-'
    },
    { key: 'palo_alto_solution', label: 'Palo Alto Solution' },
    { 
      key: 'palo_alto_pricing', 
      label: 'Palo Alto Pricing',
      type: 'number',
      format: (value) => value ? `$${value.toLocaleString()}` : '-'
    },
    { key: 'crowd_strike_solution', label: 'CrowdStrike Solution' },
    { 
      key: 'crowd_strike_pricing', 
      label: 'CrowdStrike Pricing',
      type: 'number',
      format: (value) => value ? `$${value.toLocaleString()}` : '-'
    },
    { key: 'fortinet_solution', label: 'Fortinet Solution' },
    { 
      key: 'fortinet_pricing', 
      label: 'Fortinet Pricing',
      type: 'number',
      format: (value) => value ? `$${value.toLocaleString()}` : '-'
    },
    { 
      key: 'target_date', 
      label: 'Target Date',
      type: 'date',
      format: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    { key: 'customer_current_product', label: 'Customer Current Product' },
    { 
      key: 'customer_current_spend', 
      label: 'Customer Current Spend',
      type: 'number',
      format: (value) => value ? `$${value.toLocaleString()}` : '-'
    }
  ];

  useEffect(() => {
    fetchCapabilities();
  }, []);

  const fetchCapabilities = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/capabilities');
      setCapabilities(data);
    } catch (error) {
      console.error('Error fetching capabilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCapabilityUpdate = async (capabilityId, updates) => {
    try {
      const updatedCapability = await apiClient.put(`/capabilities/${capabilityId}`, updates);
      setCapabilities(capabilities.map(c =>
        c.capability_id === capabilityId ? updatedCapability : c
      ));
      return true; // Success
    } catch (error) {
      console.error('Error updating capability:', error);
      throw error;
    }
  };

  const handleCapabilityAdd = async (capabilityData) => {
    try {
      const newCapability = await apiClient.post('/capabilities', capabilityData);
      setCapabilities([...capabilities, newCapability]);
      return true; // Success
    } catch (error) {
      console.error('Error adding capability:', error);
      throw error;
    }
  };

  const handleCapabilityDelete = async (capabilityId) => {
    try {
      await apiClient.del(`/capabilities/${capabilityId}`);
      setCapabilities(capabilities.filter(c => c.capability_id !== capabilityId));
      return true; // Success
    } catch (error) {
      console.error('Error deleting capability:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="content-area">
        <div className="capabilities-loading">Loading capabilities...</div>
      </div>
    );
  }

  return (
    <div className="content-area capabilities-content">
      <div className="capabilities-header-actions">
        <button
          onClick={() => setShowImport(true)}
          className="capabilities-button capabilities-button-import"
        >
          📥 Import from Excel
        </button>
      </div>

      <EditableDataTable
        data={capabilities}
        columns={capabilityColumns}
        idField="capability_id"
        
        // Feature flags
        enableEdit={true}
        enableSort={true}
        enableFilter={true}
        enableColumnSelector={true}
        enableDelete={true}
        enableAdd={true}
        enableSearch={true}
        enableLock={true}
        
        // Callbacks
        onUpdate={handleCapabilityUpdate}
        onDelete={handleCapabilityDelete}
        onAdd={handleCapabilityAdd}
        
        // Customization
        title="Security Capabilities Matrix"
        emptyMessage="No capabilities added yet. Click 'Add New' or import from Excel."
        searchPlaceholder="Search capabilities..."
        addButtonText="Add Capability"
        defaultVisibleColumns={[
          'name', 
          'category', 
          'source', 
          'cal_secure_phase', 
          'business_value', 
          'description'
        ]}
        lockedColumns={['name']} // Name is always visible
        nonEditableColumns={['capability_id']} // ID field cannot be edited
        
        // Custom class for styling
        className="capabilities-table-enhanced"
      />

      {showImport && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <ImportCapabilities
              onImportComplete={() => {
                setShowImport(false);
                fetchCapabilities();
              }}
            />
            <div className="modal-actions">
              <button
                onClick={() => setShowImport(false)}
                className="modal-button-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Capabilities;